import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedCounter from './animations/AnimatedCounter';
import RippleButton from './animations/RippleButton';
import ParticleBackground from './animations/ParticleBackground';
import MorphCard from './animations/MorphCard';
import { FadeInUp, ScaleOnHover } from './animations/PageTransition';
import { StickyHeader, EnhancedNavLink, EnhancedMobileMenu, EnhancedMobileMenuItem } from './animations/EnhancedNavigation';
import { ProgressiveReveal, ScrollTriggerSection } from './animations/ScrollAnimations';
import { TouchFeedback } from './animations/MobileEnhancements';
import PrizeShowcase from './animations/PrizeShowcase';
import ConfettiEffect, { SparkleEffect, CoinSparkle } from './animations/ConfettiEffect';
import CountdownTimer from './animations/CountdownTimer';
import ProgressBar from './animations/ProgressBar';
import DynamicHeroBanner from './animations/DynamicHeroBanner';
import { 
  Trophy, 
  Users, 
  Shield, 
  Clock, 
  CheckCircle, 
  Star, 
  Award, 
  Gift, 
  Coins,
  FileText,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Target,
  UserCheck,
  Zap,
  CreditCard,
  GamepadIcon,
  Bell,
  BarChart,
  RefreshCcw,
  Menu,
  X
} from 'lucide-react';
import { mockData } from '../utils/mock';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [stats, setStats] = useState({ users: 0, contests: 0, prizes: 0 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Simulate loading stats
    const timer = setTimeout(() => {
      setStats(mockData.stats);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleRegisterNow = () => {
    if (user) {
      // If user is already logged in, go to dashboard
      navigate('/dashboard');
    } else {
      // Trigger auth modal for registration/login
      window.dispatchEvent(new Event('auth-trigger'));
    }
  };

  const handleHowItWorks = () => {
    // Smooth scroll to how it works section
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false); // Close mobile menu first
    
    if (sectionId.startsWith('#')) {
      // Wait for menu to close, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      navigate(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Enhanced Header */}
      <StickyHeader>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div 
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="bg-red-600 p-2 rounded-lg">
                <Trophy className="h-8 w-8 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900">WinQuizz</span>
            </motion.div>

            {/* Enhanced Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <EnhancedNavLink 
                href="#how-it-works"
                onClick={(e) => { e.preventDefault(); handleNavigation('#how-it-works'); }}
              >
                How It Works
              </EnhancedNavLink>
              <EnhancedNavLink 
                href="#features"
                onClick={(e) => { e.preventDefault(); handleNavigation('#features'); }}
              >
                Features
              </EnhancedNavLink>
              <EnhancedNavLink 
                href="#sample-quizzes"
                onClick={(e) => { e.preventDefault(); handleNavigation('#sample-quizzes'); }}
              >
                Try Demo
              </EnhancedNavLink>
              <EnhancedNavLink 
                href="#contact"
                onClick={(e) => { e.preventDefault(); handleNavigation('#contact'); }}
              >
                Contact
              </EnhancedNavLink>
            </nav>

            {/* Desktop Register Button */}
            <div className="hidden md:block">
              <RippleButton onClick={handleRegisterNow} className="bg-red-600 hover:bg-red-700 text-white">
                Register Now
              </RippleButton>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6 text-gray-700" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6 text-gray-700" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          {/* Enhanced Mobile Navigation Menu */}
          <EnhancedMobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
            <EnhancedMobileMenuItem onClick={() => handleNavigation('#how-it-works')}>
              How It Works
            </EnhancedMobileMenuItem>
            <EnhancedMobileMenuItem onClick={() => handleNavigation('#features')}>
              Features
            </EnhancedMobileMenuItem>
            <EnhancedMobileMenuItem onClick={() => handleNavigation('#sample-quizzes')}>
              Try Demo
            </EnhancedMobileMenuItem>
            <EnhancedMobileMenuItem onClick={() => handleNavigation('#contact')}>
              Contact
            </EnhancedMobileMenuItem>
            
            <motion.div 
              className="pt-4 border-t border-gray-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <RippleButton 
                onClick={handleRegisterNow} 
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Register Now
              </RippleButton>
            </motion.div>
          </EnhancedMobileMenu>
        </div>
      </StickyHeader>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 to-red-100 py-20 hero-pattern overflow-hidden">
        <ParticleBackground count={15} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <FadeInUp delay={0.2}>
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              >
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  WinQuizz –
                </motion.span>
                <motion.span 
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
                  className="text-red-600 block"
                >
                  Participate Smartly. Win Big.
                </motion.span>
              </motion.h1>
            </FadeInUp>
            
            <FadeInUp delay={0.4}>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                India's first ultimate skill-based contest platform. Compete in fun quizzes and win amazing prizes.
              </p>
            </FadeInUp>
            
            <FadeInUp delay={0.6}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
                <RippleButton
                  onClick={handleRegisterNow}
                  size="lg"
                  className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-lg font-semibold rounded-lg whitespace-nowrap"
                >
                  <Target className="h-5 w-5 mr-2 inline-block align-left " />
                  <span className="align-middle">Register Now</span>
                </RippleButton>

                <RippleButton 
                  onClick={handleHowItWorks}
                  variant="outline" 
                  size="lg"
                  className="flex items justify-center border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-4 text-lg"
                >
                  How It Works
                </RippleButton>
              </div>
            </FadeInUp>
            
            <FadeInUp delay={0.8}>
              <div className="grid grid-cols-3 gap-8 mb-12">
                <SparkleEffect count={2} color="red">
                  <div className="text-center cursor-pointer">
                    <div className="text-3xl font-bold text-red-600">
                      <AnimatedCounter value={stats.users} suffix="+" delay={500} />
                    </div>
                    <div className="text-gray-600">Participants</div>
                  </div>
                </SparkleEffect>
                <SparkleEffect count={2} color="blue">
                  <div className="text-center cursor-pointer">
                    <div className="text-3xl font-bold text-red-600">
                      <AnimatedCounter value={stats.contests} suffix="+" delay={800} />
                    </div>
                    <div className="text-gray-600">Quizzes</div>
                  </div>
                </SparkleEffect>
                <SparkleEffect count={2} color="green">
                  <div className="text-center cursor-pointer">
                    <div className="text-3xl font-bold text-red-600">
                      <AnimatedCounter value={stats.prizes} prefix="₹" suffix="L+" delay={1100} />
                    </div>
                    <div className="text-gray-600">Prizes Won</div>
                  </div>
                </SparkleEffect>
              </div>
            </FadeInUp>

            {/* Enhanced Prize Showcase Section - Redesigned */}
            <FadeInUp delay={1}>
              <div className="mb-8">
                {/* Prize Showcase Header with Exciting Elements */}
                <div className="text-center mb-8">
                  {/* Exciting Prizes Tag */}
                  <motion.div 
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-4 shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ✨ EXCITING PRIZES AWAIT ✨
                  </motion.div>

                  <motion.p 
                    className="text-xl text-gray-600 leading-relaxed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Your dream prize is just one perfect quiz away! 🎯
                  </motion.p>
                </div>

                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ 
                    y: -8,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
                  className="relative max-w-2xl mx-auto"
                >
                  {/* Enhanced Prize Showcase with Confetti Effect */}
                  <ConfettiEffect trigger="hover" duration={2000} particleCount={50}>
                    <div className="relative">
                      {/* Glowing Ring Effect */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-r from-red-400/20 via-pink-400/20 to-red-400/20 rounded-3xl blur-xl"
                        animate={{ 
                          scale: [1, 1.05, 1],
                          opacity: [0.3, 0.6, 0.3]
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                      <PrizeShowcase className="w-full relative z-10" />
                    </div>
                  </ConfettiEffect>
                </motion.div>

                {/* Pro Strategy Tip - Moved Below Prize Showcase */}
                <motion.div 
                  className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 max-w-3xl mx-auto border border-blue-200 shadow-lg"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Trophy className="w-6 h-6 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-800">💡 Pro Winning Strategy</span>
                    <Trophy className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-blue-700 text-lg leading-relaxed text-center mb-4">
                    <strong>Perfect Score + Lightning Speed = Maximum Rewards!</strong><br/>
                    Master your knowledge, sharpen your reflexes, and claim your dream prize!
                  </p>
                  <div className="flex items-center justify-center gap-6 text-sm text-blue-600">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4" /> Speed Matters
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4" /> Accuracy Counts
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4" /> Practice Pays
                    </span>
                  </div>
                </motion.div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to start winning</p>
          </div>
          <ProgressiveReveal className="grid md:grid-cols-3 gap-8" stagger={0.15}>
            {mockData.howItWorks.map((step, index) => (
              <MorphCard key={index} className="text-center border-2 border-gray-100 hover:border-red-200 group h-full">
                <CardHeader>
                  <ScaleOnHover scale={1.1} rotate={5}>
                    <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <step.icon className="h-8 w-8 text-white" />
                    </div>
                  </ScaleOnHover>
                  <CardTitle className="text-xl text-gray-900">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </MorphCard>
            ))}
          </ProgressiveReveal>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;